import type { ClientMsg } from '../shared/protocol.ts';

type Handler = (m: any) => void;

/** An open socket that brings nothing for this long is treated as dropped. */
export const STALL_MS = 45_000;
/** What this page's messages can be (server/main.ts): 1 field patches, 2 Kinhall patches, 3 walk entries (wilds, trainers on the hunt). */
const PROTO = 3;

/** This tab's id, kept across reloads of the tab, so the server lets a reloaded tab keep playing (server/tabs.ts). */
function tabId(): string {
  try {
    let id = sessionStorage.getItem('mi-tab');
    if (!id) { id = Math.random().toString(36).slice(2, 12); sessionStorage.setItem('mi-tab', id); }
    return id;
  } catch { return ''; }
}

export class Net {
  private ws: WebSocket | null = null;
  private handler: Handler;
  private token: string;
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** When the next reconnect attempt runs (ms epoch), or 0 while connected or connecting. */
  retryAt = 0;
  onAuthFail: () => void = () => {};
  onStatus: (up: boolean) => void = () => {};

  /** When the open socket last brought anything (ms epoch). */
  private lastIn = 0;

  constructor(token: string, handler: Handler) {
    this.token = token;
    this.handler = handler;
    this.open();
    // A socket can stay "open" on a connection that stopped carrying data (plane or hotel wifi):
    // the server sends at least the online count every 30 s, so silence for longer means it is dead.
    setInterval(() => {
      const ws = this.ws;
      if (ws?.readyState === WebSocket.OPEN && Date.now() - this.lastIn > STALL_MS) this.dropStalled(ws);
    }, 5_000);
  }

  /** Give up on a socket that went quiet and start a fresh one, as a closed socket does. */
  private dropStalled(ws: WebSocket) {
    ws.onclose = null; ws.onmessage = null;
    try { ws.close(); } catch { /* already gone */ }
    if (this.ws === ws) this.ws = null;
    this.reconnectLater();
  }

  private reconnectLater() {
    const wait = Math.min(10_000, 500 * 2 ** this.retry++);
    this.retryAt = Date.now() + wait;
    this.timer = setTimeout(() => this.retryNow(), wait);
    this.onStatus(false);
  }

  private open() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(this.token)}&tab=${tabId()}&pv=${PROTO}`);
    this.ws = ws;
    ws.onopen = () => { this.retry = 0; this.retryAt = 0; this.lastIn = Date.now(); this.onStatus(true); };
    ws.onmessage = (e) => {
      this.lastIn = Date.now();
      const m = JSON.parse(e.data);
      if (m.t === 'auth') { this.onAuthFail(); return; }
      this.handler(m);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.reconnectLater();
    };
  }

  send(m: ClientMsg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  /** Skip the rest of the backoff and try to connect straight away. */
  retryNow() {
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null; this.retryAt = 0;
    this.open();
  }

  close() { const ws = this.ws; this.ws = null; ws?.close(); }
}
